import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Failure,
	Functions,
	HtmlTranscribe,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxTranscribeTranscribe
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import HtmlExtractionDialogFields from "../../field/export/HtmlExtractionDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import {Dropdown} from "primereact/dropdown";
import PageRange from "../../../../shared/components/PageRange";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import {InputNumber} from "primereact/inputnumber";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface HtmlExtractionDialogStatus extends PortalDialogState<ToolboxTranscribeTranscribe> {
	pages: number;
	currentPage: string;
}

export class HtmlExtractionDialogDefinition extends PortalDialog<ToolboxTranscribeTranscribe,
	PortalDialogProperties<ToolboxTranscribeTranscribe>, HtmlExtractionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<HtmlExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.transcribeDocument, Functions.getTranscribeDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_HTML, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-html-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_html.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxTranscribeTranscribe | undefined = this.state.parameter?.data;
		let errors: Map<HtmlExtractionDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(HtmlExtractionDialogFields.ERROR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-error">
							<Translate {...this.props} data="extract_html.error.label" namespace="dialog"/>
							<Info {...this.props} data="extract_html.error.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<Dropdown inputId="control-error"
									  value={FunctionHelper.applyValue(
										  parameter?.failureLevel, ToolboxTranscribeTranscribe.getFailureLevelDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_html.error.warning", {ns: "dialog"}
											  ),
											  value: Failure.Warning
										  },
										  {
											  label: Translate.value(
												  "extract_html.error.missing_content", {ns: "dialog"}
											  ),
											  value: Failure.MissingContent
										  },
										  {
											  label: Translate.value(
												  "extract_html.error.error", {ns: "dialog"}
											  ),
											  value: Failure.Error
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HtmlExtractionDialogFields.ERROR, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(HtmlExtractionDialogFields.DPI)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-dpi">
							<Translate {...this.props} data="extract_html.dpi" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-dpi" step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.html?.dpi, HtmlTranscribe.getDpiDefault()
										 )}
										 max={HtmlTranscribe.getDpiMax()} min={HtmlTranscribe.getDpiMin()}
										 onChange={(e) => this.onChange(
											 HtmlExtractionDialogFields.DPI, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(HtmlExtractionDialogFields.PAGERANGE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="extract_html.pages.label" namespace="dialog"/>
							<Info {...this.props} data="extract_html.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(HtmlExtractionDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.html?.pages, HtmlTranscribe.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   HtmlExtractionDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")} editable={true}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(HtmlExtractionDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(HtmlExtractionDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(HtmlExtractionDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(HtmlExtractionDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: HtmlExtractionDialogFields, value: any): void {
		let parameter: ToolboxTranscribeTranscribe = this.state.parameter!.data;
		let errors: Map<HtmlExtractionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case HtmlExtractionDialogFields.ERROR:
				parameter.failureLevel = value;
				break;
			case HtmlExtractionDialogFields.DPI:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HtmlTranscribe.getDpiDefault();
				}

				parameter.html!.dpi = value;
				break;
			case HtmlExtractionDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.html!.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-export-html small"}/>
					<Translate {...this.props} data="extract_html.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxTranscribeTranscribe | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.html!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			transcribe: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: WebServiceTypes.TOOLBOX,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.extract.html", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_HTML;
	}

	protected getDefaultParameter(): Parameter<ToolboxTranscribeTranscribe> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const HtmlExtractionDialog = React.memo(HtmlExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxTranscribeTranscribe>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxTranscribeTranscribe>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default HtmlExtractionDialog;